import type { Components } from '@/api/generated/api';
import type { ITimelineRailPosition } from '@/core/timeline/timeline.types';
import { dateToTimelineWeekKey } from '@/utils/date.timeline.utils';

type TTimelineSchoolClassState = Pick<Components.Schemas.SchoolClassDto, 'archivedAt'>;

export const areTimelineActionsDisabled = (
  schoolClass: TTimelineSchoolClassState | null | undefined,
  isSchoolContext: boolean,
): boolean => schoolClass == null || schoolClass.archivedAt != null || isSchoolContext;

export interface ITimelineEmptyStateFilters {
  hasSearchQuery: boolean;
  hasSchoolSubjectFilter: boolean;
  hasGroupingFilter: boolean;
}

/** Search and grouping narrow the feed ("nothing matched"); a subject alone only scopes it ("no entries"). */
export const resolveTimelineEmptyState = (
  filters: ITimelineEmptyStateFilters,
  t: (key: string) => string,
): { title: string; message: string } => {
  if (filters.hasSearchQuery || filters.hasGroupingFilter) {
    return {
      title: t('performanceAssessmentEntry.filteredEmptyStateTitle'),
      message: t('performanceAssessmentEntry.filteredEmptyStateDescription'),
    };
  }
  return {
    title: t(filters.hasSchoolSubjectFilter ? 'general.noEntries' : 'general.noEntriesYet'),
    message: t('performanceAssessmentEntry.emptyStateDescription'),
  };
};

export const getTimelineRailPosition = (index: number, itemCount: number): Required<ITimelineRailPosition> => ({
  showRail: true,
  showLineAbove: index > 0,
  showLineBelow: index < itemCount - 1,
});

/** Groups already ordered timeline items without changing their item or group order. */
export const groupOrderedTimelineItemsByWeek = <T>(
  items: readonly T[],
  getDate: (item: T) => string,
): Array<[string, T[]]> => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const groupKey = dateToTimelineWeekKey(getDate(item));
    const group = groups.get(groupKey);
    if (group) group.push(item);
    else groups.set(groupKey, [item]);
  }
  return [...groups.entries()];
};
